/*
 * Photo Headshot — composes the chosen design around the cropped photo and
 * writes it onto the slide, replacing the shape the user selected.
 *
 * There's no Office.js call that inserts an image shape directly (no
 * `addPicture`/`addImage` on PowerPoint's `ShapeCollection` in this
 * project's API surface — see photoHeadSelection.ts's header comment), so
 * images go in the same way the generic insertImageOnSlide() helper
 * (integrations/powerpoint/host/slideOps.ts) does:
 * `Office.context.document.setSelectedDataAsync` with `CoercionType.Image`,
 * which appends it as the slide's last shape, followed by a `PowerPoint.run`
 * to reposition that shape to where the design calls for it.
 *
 * Z-order (each step's shapes render on top of the previous): frame/card
 * background -> photo -> bio-card text fields. The original selected shape
 * is deleted last, once the replacement is fully on the slide.
 */

import { hasPpt, ppt } from "../../../utils/formatUtils";
import { getErrorMessage, logPhotoHeadError } from "./photoHeadApi";
import type { PhotoHeadDesign, PhotoHeadField, SelectedPhotoShape } from "../../../types";

const PPT_ONLY_MESSAGE = "This tool works only in PowerPoint.";
const POINTS_PER_INCH = 72;

function hexColor(hex: string): string {
  return hex.startsWith("#") ? hex : `#${hex}`;
}

/** Insert `imageBase64` as the slide's last shape, then move/size it there. */
async function insertPhotoAt(
  imageBase64: string,
  rect: { left: number; top: number; width: number; height: number },
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(
      imageBase64,
      { coercionType: Office.CoercionType.Image, imageWidth: rect.width, imageHeight: rect.height },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
        else reject(new Error(result.error.message));
      },
    );
  });

  await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedSlides();
    selected.load("items");
    await context.sync();
    const slide = selected.items[0];
    if (!slide) return;
    slide.shapes.load("items");
    await context.sync();
    const inserted = slide.shapes.items[slide.shapes.items.length - 1];
    if (!inserted) return;
    inserted.left = rect.left;
    inserted.top = rect.top;
    inserted.width = rect.width;
    inserted.height = rect.height;
    await context.sync();
  });
}

/** Photo-only design: a border/frame shape sized to the photo, behind it. */
function addFrame(
  slide: PowerPoint.Slide,
  design: PhotoHeadDesign,
  rect: { left: number; top: number; width: number; height: number },
): void {
  const geometricType =
    design.shape === "OVAL" ? PowerPoint.GeometricShapeType.ellipse : PowerPoint.GeometricShapeType.roundRectangle;
  const frame = slide.shapes.addGeometricShape(geometricType);
  frame.left = rect.left;
  frame.top = rect.top;
  frame.width = rect.width;
  frame.height = rect.height;
  frame.fill.clear();
  if (design.borderColor) {
    frame.lineFormat.visible = true;
    frame.lineFormat.color = hexColor(design.borderColor);
    frame.lineFormat.weight = design.borderWidth;
  } else {
    frame.lineFormat.visible = false;
  }
  frame.name = `PhotoHead_Frame_${design.id}`;
}

/** Bio-card design: the card background (and optional accent bar) behind the photo+text. */
function addCardBackground(
  slide: PowerPoint.Slide,
  design: PhotoHeadDesign,
  card: { left: number; top: number; width: number; height: number },
): void {
  const template = design.template!;
  const bg = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.roundRectangle);
  bg.left = card.left;
  bg.top = card.top;
  bg.width = card.width;
  bg.height = card.height;
  bg.fill.setSolidColor(hexColor(template.cardBg));
  bg.lineFormat.visible = false;
  bg.name = `PhotoHead_Card_${design.id}`;

  if (template.accentBar) {
    const bar = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle);
    bar.left = card.left;
    bar.top = card.top;
    bar.width = card.width;
    bar.height = Math.max(4, card.height * 0.06);
    bar.fill.setSolidColor(hexColor(design.borderColor ?? "FFFFFF"));
    bar.lineFormat.visible = false;
    bar.name = `PhotoHead_AccentBar_${design.id}`;
  }
}

/** Bio-card design: stacked name/title/detail placeholder text boxes next to (or under) the photo. */
function addCardText(
  slide: PowerPoint.Slide,
  design: PhotoHeadDesign,
  card: { left: number; top: number; width: number; height: number },
  photo: { left: number; top: number; width: number; height: number },
): void {
  const template = design.template!;
  const isTop = template.layout === "top";
  const textLeft = isTop ? card.left + 6 : photo.left + photo.width + 10;
  const textWidth = isTop ? card.width - 12 : card.left + card.width - textLeft - 6;
  const textTop0 = isTop ? photo.top + photo.height + 6 : card.top + (card.height - fieldsHeight(template.fields)) / 2;

  let y = textTop0;
  for (const field of template.fields) {
    const box = slide.shapes.addTextBox(field.default);
    box.left = textLeft;
    box.top = y;
    box.width = Math.max(20, textWidth);
    box.height = field.fontSize * 1.8;
    box.name = `PhotoHead_${field.key}_${design.id}`;
    try {
      const range = box.textFrame.textRange;
      range.font.size = field.fontSize;
      range.font.bold = field.bold;
      range.font.color = hexColor(field.color);
      if (isTop) box.textFrame.textRange.paragraphFormat.horizontalAlignment = PowerPoint.ParagraphHorizontalAlignment.center;
    } catch (error) {
      // Font/alignment API may be unavailable on this host — the text still lands, just unstyled.
      logPhotoHeadError("photo-head-card-text-style", getErrorMessage(error, "Font/alignment styling unavailable"));
    }
    y += field.fontSize * 1.8 + 2;
  }
}

function fieldsHeight(fields: readonly PhotoHeadField[]): number {
  return fields.reduce((sum, f) => sum + f.fontSize * 1.8 + 2, 0);
}

/** Square photo rect inscribed in the original shape's bounding box, centered. */
function photoOnlyRect(shape: SelectedPhotoShape): { left: number; top: number; width: number; height: number } {
  const diameter = Math.min(shape.width, shape.height);
  const centerX = shape.left + shape.width / 2;
  const centerY = shape.top + shape.height / 2;
  return { left: centerX - diameter / 2, top: centerY - diameter / 2, width: diameter, height: diameter };
}

/** Card + photo rects for a bio-card design, centered on the original shape. */
function bioCardRects(
  design: PhotoHeadDesign,
  shape: SelectedPhotoShape,
): { card: { left: number; top: number; width: number; height: number }; photo: { left: number; top: number; width: number; height: number } } {
  const template = design.template!;
  const centerX = shape.left + shape.width / 2;
  const centerY = shape.top + shape.height / 2;
  const cardWidth = template.cardWidth * POINTS_PER_INCH;
  const photoSize = template.photoSize * POINTS_PER_INCH;
  const cardHeight =
    template.layout === "top"
      ? photoSize + template.cardHeight * POINTS_PER_INCH * 0.6
      : template.cardHeight * POINTS_PER_INCH;

  const card = { left: centerX - cardWidth / 2, top: centerY - cardHeight / 2, width: cardWidth, height: cardHeight };
  const photo =
    template.layout === "top"
      ? { left: card.left + (cardWidth - photoSize) / 2, top: card.top + 8, width: photoSize, height: photoSize }
      : { left: card.left + 10, top: card.top + (cardHeight - photoSize) / 2, width: photoSize, height: photoSize };

  return { card, photo };
}

/** Delete the shape the user originally selected, now replaced by the composed design. */
async function deleteOriginalShape(shapeId: string): Promise<void> {
  await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedSlides();
    selected.load("items");
    await context.sync();
    const slide = selected.items[0];
    if (!slide) return;
    slide.shapes.load("items/id");
    await context.sync();
    const original = slide.shapes.items.find((s) => String(s.id) === shapeId);
    original?.delete();
    await context.sync();
  });
}

/**
 * Compose `design` around the already-face-cropped `photoBase64` and write it
 * onto the slide in place of `originalShape`.
 */
export async function insertPhotoHead(
  originalShape: SelectedPhotoShape,
  design: PhotoHeadDesign,
  photoBase64: string,
): Promise<void> {
  if (!hasPpt()) throw new Error(PPT_ONLY_MESSAGE);

  if (design.template) {
    const { card, photo } = bioCardRects(design, originalShape);
    await ppt().run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load("items");
      await context.sync();
      const slide = selected.items[0];
      if (!slide) throw new Error("No slide selected");
      addCardBackground(slide, design, card);
      await context.sync();
    });
    await insertPhotoAt(photoBase64, photo);
    await ppt().run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load("items");
      await context.sync();
      const slide = selected.items[0];
      if (!slide) return;
      addCardText(slide, design, card, photo);
      await context.sync();
    });
  } else {
    const rect = photoOnlyRect(originalShape);
    await ppt().run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load("items");
      await context.sync();
      const slide = selected.items[0];
      if (!slide) throw new Error("No slide selected");
      addFrame(slide, design, rect);
      await context.sync();
    });
    await insertPhotoAt(photoBase64, rect);
  }

  await deleteOriginalShape(originalShape.shapeId);
}
